import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    DialogTitle,
    useMediaQuery,
    useTheme,
    Chip,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const CustomIndexDialog = ({ open, onClose, indices, onSelect, initialType, initialCustom }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isArabic = i18n.language === 'ar';

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('All');
    const [selectedType, setSelectedType] = useState(initialType || 'All');
    const [showCustomOnly, setShowCustomOnly] = useState(initialCustom || false);

    useEffect(() => {
        if (open) {
            setSelectedType(initialType || 'All');
            setShowCustomOnly(initialCustom || false);
        }
    }, [open, initialType, initialCustom]);

    const countries = useMemo(() => {
        const uniqueCountries = new Set();
        indices.forEach(idx => {
            if (idx.country) uniqueCountries.add(idx.country);
        });
        return ['All', ...Array.from(uniqueCountries).sort()];
    }, [indices]);

    const types = useMemo(() => {
        const uniqueTypes = new Set();
        indices.forEach(idx => {
            if (isArabic) {
                if (idx.type_ar) uniqueTypes.add(idx.type_ar);
                else if (idx.type) uniqueTypes.add(idx.type);
            } else {
                if (idx.type) uniqueTypes.add(idx.type);
            }
        });
        return ['All', ...Array.from(uniqueTypes).sort()];
    }, [indices, isArabic]);

    const filteredIndices = useMemo(() => {
        return indices.filter(idx => {
            const matchesSearch = (idx.name && idx.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                (idx.name_ar && idx.name_ar.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const matchesCountry = selectedCountry === 'All' || idx.country === selectedCountry;
            
            const matchesType = selectedType === 'All' || 
                               (isArabic ? (idx.type_ar === selectedType || idx.type === selectedType) : idx.type === selectedType);
            
            const matchesCustom = !showCustomOnly || idx.custom === true;

            return matchesSearch && matchesCountry && matchesType && matchesCustom;
        });
    }, [indices, searchTerm, selectedCountry, selectedType, showCustomOnly, isArabic]);

    const handleSelect = (idx) => {
        onSelect(idx.category, idx.type, idx.name, idx.id);
        onClose();
    };

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            sx={{ width: "100vw", sm: "auto" }}
            PaperProps={{
                sx: {
                    height: isMobile ? "100%" : "85vh",
                    maxHeight: isMobile ? "100%" : "85vh",
                    borderRadius: isMobile ? 0 : '16px',
                },
            }}
        >
            <DialogTitle sx={{ 
                pt: isMobile ? 'max(24px, env(safe-area-inset-top))' : undefined,
                fontWeight: 'bold',
                color: '#054e73'
            }}>
                {t('CustomIndex.title')}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: isMobile ? 'max(16px, env(safe-area-inset-top))' : 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {/* Filters Section */}
                <Box sx={{ mb: 3, mt: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={t('CustomIndex.search_placeholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('CustomIndex.filter_country')}</InputLabel>
                                <Select
                                    value={selectedCountry}
                                    onChange={(e) => setSelectedCountry(e.target.value)}
                                    label={t('CustomIndex.filter_country')}
                                >
                                    {countries.map((country) => (
                                        <MenuItem key={country} value={country}>
                                            {country === 'All' ? t('CustomIndex.all_countries') : (t(`countries.${country}`) || country)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('CustomIndex.filter_type')}</InputLabel>
                                <Select
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    label={t('CustomIndex.filter_type')}
                                >
                                    {types.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type === 'All' ? t('CustomIndex.all_types') : type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={showCustomOnly} 
                                        onChange={(e) => setShowCustomOnly(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={t('CustomIndex.filter_custom')}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Results Section */}
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    {filteredIndices.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <Typography color="text.secondary">
                                {t('CustomIndex.no_results')}
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredIndices.map((idx) => (
                                <Grid item xs={12} sm={6} md={4} key={idx.id}>
                                    <Card 
                                        sx={{ 
                                            height: '100%', 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                            },
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '12px'
                                        }}
                                        onClick={() => handleSelect(idx)}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                                    {isArabic ? idx.category_ar : idx.category}
                                                </Typography>
                                                {idx.custom && (
                                                    <Chip 
                                                        label={t('CustomIndex.custom_badge')} 
                                                        size="small" 
                                                        color="secondary" 
                                                        sx={{ height: '20px', fontSize: '0.65rem', fontWeight: 'bold' }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: '#333' }}>
                                                {isArabic ? (idx.name_ar || idx.name) : idx.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip 
                                                    label={isArabic ? (idx.type_ar || idx.type) : idx.type} 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                                {idx.country && (
                                                    <Chip 
                                                        label={t(`countries.${idx.country}`) || idx.country} 
                                                        size="small" 
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CustomIndexDialog;
